import { getHighestRole } from '@paintscout/util'
import { formatName, getCustomPermissionLabels } from '@paintscout/util/builder'
import { useIsMobile } from '@paintscout/ui'
import { useClientOptions, useUser, useUserPermissions, useUsers } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useEffect, useRef } from 'react'
import { Sentry } from 'src/util/appSentry'
import userflow from 'userflow.js'

import moment from 'moment'

export default function UserFlow() {
  const { user, isSuperadmin, nonBilling, isTrial, preferences } = useUser()
  const { users } = useUsers()
  const { permissionToUpdateBilling } = useUserPermissions()
  const { clientId, options } = useClientOptions()
  const mobile = useIsMobile()
  const screenWidth = window?.screen?.width || 0
  const { crm: crmFlag, sherwinWilliams } = useFlags()
  const userType = getHighestRole(user.app_metadata.roles)
  const crmTrialExpiration = options?.options?.crm?.trialExpiration
  const isOnCrmTrial = crmTrialExpiration && crmFlag ? moment().isBefore(moment(crmTrialExpiration)) : false
  const leadSource = user?.app_metadata?.company?.leadSource
  const owner = user?.app_metadata?.isOwner

  const hasCreatedQuote = options?.options?.onboardingTasks?.hasCreatedQuote || false
  const hasCreatedDeal = options?.options?.onboardingTasks?.hasCreatedDeal || false
  const hasCreatedEvent = options?.options?.onboardingTasks?.hasCreatedEvent || false
  const hasCreatedProductionEvent = options?.options?.onboardingTasks?.hasCreatedProductionEvent || false

  const productOptions = Object.values(options?.options?.products?.values || {})
  const hasSherwinWilliamsProduct = productOptions.some((product) => product.manufacturer === 'Sherwin-Williams')

  const hasActiveCrewUsers = users.some(
    (u) => getHighestRole(u.app_metadata?.roles) === 'crew' && u.app_metadata.active
  )

  const getLifecycleStage = () => {
    if (nonBilling) {
      return 'Non Billing'
    }
    if (isTrial) {
      return 'Trial'
    }
    if (user?.app_metadata?.company?.active) {
      return 'Customer'
    }
    if (!user?.app_metadata?.company?.active) {
      return 'Past Customer'
    }
  }

  const lastCrmFlagRef = useRef<boolean | null>(null)
  const userflowInitRef = useRef(false)

  useEffect(() => {
    if (isSuperadmin || !process.env.USER_FLOW_TOKEN) {
      return
    }

    const run = async () => {
      const lifecycleStage = getLifecycleStage()

      // Prepare payloads
      const identityPayload = JSON.stringify({
        name: formatName(user.user_metadata),
        'First Name': user.user_metadata.firstName,
        'Last Name': user.user_metadata.lastName,
        email: user.email,
        'User Type': userType,
        Active: user.app_metadata.active ? 'Yes' : 'No',
        'Device Type': mobile ? 'Mobile' : 'Desktop',
        'New Look': 'Yes',
        'CRM Enabled': crmFlag ? 'Yes' : 'No',
        'Production Enabled': crmFlag ? 'Yes' : 'No',
        'PaintScout User ID': user.user_id,
        'Side Nav Showing': screenWidth > 1213 && userType !== 'painter' ? 'Yes' : 'No',
        'Company ID': clientId,
        'CRM Trial': isOnCrmTrial ? 'Yes' : 'No',
        'SW Product Browser Enabled': sherwinWilliams ? 'Yes' : 'No',
        'Logo Uploaded': options?.options.logo?.value ? 'Yes' : 'No',
        'Lead Source': leadSource,
        'Integrations Enabled': Object.keys(preferences?.integrations || {}),
        'Permission to Update Billing': permissionToUpdateBilling ? 'Yes' : 'No',
        'Custom Permissions': getCustomPermissionLabels(user?.app_metadata?.customPermissions),
        Owner: owner ? 'Yes' : 'No'
      })
      const groupPayload = JSON.stringify({
        name: options?.options.companyName.value,
        Active: user.app_metadata.company?.active ? 'Yes' : 'No',
        'Billing ID': user.app_metadata?.company?.billingId,
        'Lifecycle Stage': lifecycleStage,
        'Parent Company ID Present': user.app_metadata?.company?.parentCompanyId ? 'Yes' : 'No',
        'Parent Company ID': user.app_metadata?.company?.parentCompanyId,
        'PaintScout Company ID': clientId,
        'Lead Source': leadSource,
        'Created Quote': hasCreatedQuote ? 'Yes' : 'No',
        'Created Deal': hasCreatedDeal ? 'Yes' : 'No',
        'Created Event': hasCreatedEvent ? 'Yes' : 'No',
        'Created Job': hasCreatedProductionEvent ? 'Yes' : 'No',
        'Has SW Product': hasSherwinWilliamsProduct ? 'Yes' : 'No',
        'Integrations Enabled': Object.keys(options?.options?.integrations || {}),
        'Has Active Crew Users': hasActiveCrewUsers ? 'Yes' : 'No',
        '# Active Users':
          userType === 'painter' ? undefined : users.filter((u) => u.app_metadata.active !== false).length
      })

      if (!userflowInitRef.current) {
        userflowInitRef.current = true
        userflow.setInferenceClassNameFilter((cls) => !cls.startsWith('Mui') && !cls.startsWith('jss'))
        userflow.setInferenceAttributeFilter('id', (_id) => false)
        userflow.setInferenceAttributeNames(['data-tour-id'])
        userflow.setUrlFilter((url) => {
          const parsed = new URL(url)
          const filtered = new URLSearchParams()
          if (parsed.searchParams.has('section')) {
            filtered.set('section', parsed.searchParams.get('section'))
          }
          parsed.search = filtered.toString()
          return parsed.toString()
        })
        userflow.setBaseZIndex(1299)
        userflow.init(process.env.USER_FLOW_TOKEN)
        userflow.setResourceCenterLauncherHidden(userType === 'painter')
      }

      if (lastCrmFlagRef.current !== crmFlag) {
        userflow.identify(user.user_id, JSON.parse(identityPayload))

        userflow.group(clientId, JSON.parse(groupPayload))
      }
      lastCrmFlagRef.current = crmFlag
    }

    run().catch((error) => {
      console.log('Userflow error:', error)
      Sentry.captureException(error)
    })
  }, [user?.user_id, clientId, crmFlag])

  return null
}
